import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@helios/authz';
import { useScopedNumericQuery } from './useScopedNumericQuery';
import { sessionScopeKey, useSessionScopeFence } from './useSessionScope';
import type { RequestOptions } from '@/services/apiClient';
import {
  commandCenterClient,
  type CommandCenterFilters,
  type CommandCenterPayload,
} from '@/services/commandCenterClient';

export function useCommandCenter(filters: CommandCenterFilters) {
  return useScopedNumericQuery<CommandCenterPayload>(
    ['dashboard', 'command-center', filters],
    PERMISSIONS.DASHBOARD_READ,
    (options) => commandCenterClient.getCommandCenter(filters, options),
    { staleTime: 15_000, refetchInterval: 30_000 },
  );
}

export function useCommandCenterActions(filters: CommandCenterFilters) {
  return {
    generateBrief: useCommandCenterMutation(PERMISSIONS.DASHBOARD_READ, 'brief', (options) =>
      commandCenterClient.generateAdvisorBrief(filters, options),
    ),
    exportReport: useCommandCenterMutation(
      PERMISSIONS.REPORTS_LIBRARY_EXECUTE,
      'export',
      (options) => commandCenterClient.exportReport(filters, options),
    ),
  };
}

function useCommandCenterMutation<T>(
  permission: string,
  name: string,
  action: (options: RequestOptions) => Promise<T>,
) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const client = useQueryClient();
  const changed = () => new Error('Executive access changed. Reload before continuing.');
  const mutation = useMutation({
    mutationKey: ['executive-action', name, ...sessionScopeKey(identity)],
    retry: false,
    onMutate: () => identity,
    mutationFn: async () => {
      if (!enabled || !isCurrent(identity)) throw changed();
      try {
        const result = await action({ sessionOrigin: identity.sessionOrigin, allowRefresh: false });
        if (!isCurrent(identity)) throw changed();
        return result;
      } catch (error) {
        if (!isCurrent(identity)) throw changed();
        throw error;
      }
    },
    onSuccess: async (_data, _variables, origin) => {
      if (!origin || !isCurrent(origin)) return;
      await client.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'dashboard' &&
          query.queryKey[1] === 'command-center' &&
          sessionScopeKey(origin).every((part, index) => query.queryKey[index + 3] === part),
      });
      if (!isCurrent(origin)) throw changed();
    },
  });
  const visible = enabled && (!mutation.context || isCurrent(mutation.context));
  return {
    ...mutation,
    data: visible ? mutation.data : undefined,
    error: visible ? mutation.error : null,
    isPending: visible && mutation.isPending,
  };
}
